/**
 * Permissions Context
 * Provides role-based access control throughout the admin panel
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/apiClient';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children, admin }) {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  // Fetch permissions when admin changes
  const fetchPermissions = useCallback(async () => {
    if (!admin?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await api.get('/admin/permissions/my-permissions');
      const data = res.data;
      setPermissions(data.permissions);
      setIsOwner(data.is_owner || data.role === 'owner');
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [admin?.access_token]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has access to a section
  const hasSection = useCallback((sectionId) => {
    if (isOwner) return true;
    if (!permissions?.sections) return false;
    return permissions.sections.includes(sectionId);
  }, [permissions, isOwner]);

  // Check if user has access to a subtab
  const hasSubtab = useCallback((sectionId, subtabId) => {
    if (isOwner) return true;
    if (!permissions?.subtabs) return false;
    const sectionSubtabs = permissions.subtabs[sectionId] || [];
    return sectionSubtabs.includes(subtabId);
  }, [permissions, isOwner]);

  // Check if user can access a specific tab (could be section or subtab)
  const canAccessTab = useCallback((tabId, parentSectionId = null) => {
    if (isOwner) return true;
    if (!permissions) return false;

    // If it's a standalone section (like dashboard)
    if (!parentSectionId) {
      // Check if it's a section
      if (permissions.sections?.includes(tabId)) return true;
      // Check if it's a subtab in any section
      for (const [section, subtabs] of Object.entries(permissions.subtabs || {})) {
        if (subtabs.includes(tabId)) return true;
      }
      return false;
    }

    // If parent section is provided, check subtab access
    return hasSubtab(parentSectionId, tabId);
  }, [permissions, isOwner, hasSubtab]);

  // Filter sections based on permissions
  const filterSections = useCallback((sections) => {
    if (isOwner) return sections;
    if (!permissions) return [];

    return sections
      .filter(section => {
        // Standalone sections (no children)
        if (section.children.length === 0) {
          return permissions.sections?.includes(section.id);
        }
        // Sections with children - show if user has access to the section
        return permissions.sections?.includes(section.id);
      })
      .map(section => {
        if (section.children.length === 0) return section;
        
        // Filter children based on subtab permissions
        const filteredChildren = section.children.filter(child => {
          // Owner-only tabs are handled separately
          if (child.ownerOnly) return isOwner;
          // Check subtab permission
          const sectionSubtabs = permissions.subtabs?.[section.id] || [];
          return sectionSubtabs.includes(child.id);
        });

        return { ...section, children: filteredChildren };
      })
      .filter(section => {
        // Remove sections that have no accessible children
        if (section.children.length === 0 && !permissions.sections?.includes(section.id)) {
          return false;
        }
        return section.children.length > 0 || permissions.sections?.includes(section.id);
      });
  }, [permissions, isOwner]);

  const value = {
    permissions,
    loading,
    isOwner,
    hasSection,
    hasSubtab,
    canAccessTab,
    filterSections,
    refetch: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
